import { INSTANCE as DeviceManager } from '@core/devices/DeviceManager'
import { ConnectionState } from '@core/devices/IDevice'
import type { SearchResponseData, SearchProvider } from '@/core/search/SearchProvider'
import { DeviceSearchProvider } from '@/core/search/DeviceSearchProvider'
import { MicroAppId } from '@core/application/ApplicationConstants'
import { Env } from '../EnvUtils'
import { INSTANCE as DevicePullUpTaskManager } from '@/core/DevicePull/DevicePullUpTaskManager'

export interface SearchChangeListener {
  onSearchListClear(): void

  onSearchListUpdated(): void

  onSearchListInit(): void

  onSearchListItemConnectUpdated(id: string): void

  onSearchStateUpdated(searchState: boolean): void
}

class SearchManager {
  #changeListeners = new Set<SearchChangeListener>()
  #initialized = false
  #providerMap = new Map<string, SearchProvider>()

  #providerStateChangeListener = {
    onDeviceConnectionStateChanged: (id: string): void => {
      this.#notifySearchListItemConnectChanged(id)
    },
    onDeviceRemoved: (id: string): void => {
      this.#providerMap.delete(id)
      this.#notifySearchListChanged()
    },
  }

  #getProviderList(): SearchProvider[] {
    return Array.from(this.#providerMap.values())
  }

  async initSearch(keyWord: string) {
    this.#initialized = false
    await this.#cleanup()

    const deviceList = DeviceManager.getDeviceList()

    for (const deviceItem of deviceList) {
      try {
        let fileManagerAppId = undefined

        if (Env.isTest()) {
          fileManagerAppId = localStorage.getItem('fileManagerAppId') ?? MicroAppId.FILE_MANAGER
        } else {
          fileManagerAppId = MicroAppId.FILE_MANAGER
        }

        if (deviceItem.connectionState !== ConnectionState.CONNECTED) {
          // 非已连接设备重新拉起，使用onDeviceListUpdated回调更新搜索结果
          DevicePullUpTaskManager.pullUpDevice({ deviceId: deviceItem.id }, undefined, false, false)
        }

        const provider = new DeviceSearchProvider(
          deviceItem.id,
          deviceItem.connectionState === ConnectionState.CONNECTED,
          this.#providerStateChangeListener,
          fileManagerAppId
        )

        provider.init(keyWord)

        this.#providerMap.set(deviceItem.id, provider)
      } catch (error) {
        console.error(error)
      }
    }
  }

  addChangeListener(listener: SearchChangeListener): void {
    if (!this.#changeListeners.has(listener)) {
      this.#changeListeners.add(listener)
    }
  }

  removeChangeListener(listener: SearchChangeListener): void {
    this.#changeListeners.delete(listener)
  }

  #notifySearchListItemConnectChanged(id: string) {
    for (const listener of this.#changeListeners) {
      listener.onSearchListItemConnectUpdated(id)
    }
  }

  #notifySearchListChanged() {
    for (const listener of this.#changeListeners) {
      listener.onSearchListUpdated()
    }
  }

  #notifySearchStateChanged(searchState: boolean) {
    for (const listener of this.#changeListeners) {
      listener.onSearchStateUpdated(searchState)
    }
  }

  #notifySearchListInit() {
    for (const listener of this.#changeListeners) {
      listener.onSearchListInit()
    }
  }

  #notifySearchListClear() {
    for (const listener of this.#changeListeners) {
      listener.onSearchListClear()
    }
  }

  async fetchSearchList() {
    this.#notifySearchStateChanged(true)

    // TODD: parallel request search results
    for (const provider of this.#getProviderList()) {
      // TODO: 搜索和翻页分开
      if (provider.hasMorePages) {
        await provider.search()
      }
      if (provider.hasMorePages) break
    }

    this.#notifySearchStateChanged(false)
    this.#notifySearchListChanged()

    if (!this.#initialized) {
      this.#initialized = true
      this.#notifySearchListInit()
    }
  }

  getSearchList() {
    let assemblyLists: SearchResponseData[] = []

    for (const provider of this.#getProviderList()) {
      const list = provider.getSearchResult()

      if (provider instanceof DeviceSearchProvider) {
        list[0].provider.isConnected = provider.getConnectState()
      }

      assemblyLists = [...assemblyLists, ...list]
    }

    return assemblyLists
  }

  getSearchResponseDataById(id: string): SearchResponseData[] | undefined {
    const provider = this.#providerMap.get(id)

    if (!provider) return undefined

    const assemblyList = provider.getSearchResult()

    if (provider instanceof DeviceSearchProvider) {
      assemblyList[0].provider.isConnected = provider.getConnectState()
    }

    return assemblyList
  }

  async clearSearchResult() {
    await this.#cleanup()
    this.#notifySearchListClear()
  }

  async #cleanup() {
    for (const provider of this.#getProviderList()) {
      await provider.destroy()
    }

    this.#providerMap.clear()
  }
}

const INSTANCE = new SearchManager()

export { INSTANCE }
